import { cn } from '@/lib/utils';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import {
    Fragment,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import {
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
    type ColumnDef,
    type ColumnSizingState,
    type SortingState,
} from '@tanstack/react-table';
import Pagination from './Pagination';

export interface DataTableColumn<T> {
    /** Stable column id (also the sort key). */
    id: string;
    header: ReactNode;
    cell: (row: T) => ReactNode;
    align?: 'left' | 'right' | 'center';
    /** Numeric column: font-mono tabular-nums + right-align by default. */
    numeric?: boolean;
    sortable?: boolean;
    /** Sort key extractor; required for the column to sort meaningfully. */
    sortValue?: (row: T) => string | number | null;
    width?: string;
    minWidth?: string;
}

export interface DataTableProps<T> {
    columns: DataTableColumn<T>[];
    data: T[];
    rowKey: (row: T) => string;
    onRowClick?: (row: T) => void;
    selectedKey?: string;
    initialSort?: { id: string; desc: boolean };
    stickyHeader?: boolean;
    /** Expanded content (chevron + row spanning all columns), animated. */
    expandable?: (row: T) => ReactNode;
    pagination?: { pageSize: number } | false;
    empty: ReactNode;
    loading?: boolean;
    ariaLabel: string;
    /** ~34px rows instead of ~42px. */
    dense?: boolean;
    className?: string;
}

const ALIGN: Record<'left' | 'right' | 'center', string> = {
    left: 'text-left',
    right: 'text-right',
    center: 'text-center',
};

function resolveAlign<T>(col: DataTableColumn<T>): 'left' | 'right' | 'center' {
    if (col.align) return col.align;
    return col.numeric ? 'right' : 'left';
}

const MIN_COLUMN_PX = 48;
const DEFAULT_COLUMN_PX = 150;

// Loading state: render this many placeholder rows so the table reads as a
// populated surface mid-fetch rather than a single "Loading…" line.
const SKELETON_ROW_COUNT = 8;
// A rotating palette of bone widths so skeleton cells look like real, varied
// content instead of a uniform grid. Indexed by (rowIndex + columnIndex).
const SKELETON_WIDTHS = ['w-3/4', 'w-1/2', 'w-2/3', 'w-5/6', 'w-2/5', 'w-1/2'];
// Default starting-width cap for a column that declares no explicit `width`:
// content-sized columns start no wider than this so a long free-text column
// doesn't blow the table out. Double-clicking the resize handle auto-fits the
// column to its full content (Excel-style), past this cap.
const DEFAULT_MAX_COLUMN_PX = 280;

// Parse a CSS width string (e.g. "200px", "200") to a pixel number; non-px
// units / unset return undefined (no declared cap).
function parsePx(value?: string): number | undefined {
    if (!value) return undefined;
    const n = Number.parseFloat(value);
    return Number.isFinite(n) ? n : undefined;
}

// The single table mechanism for the kit. TanStack Table headless under kit
// styling, rendered as a semantic <table> inside an overflow-x-auto wrapper.
// Client sorting (with indicators), optional integrated Pagination, expandable
// rows, row click/selection, loading + empty states.
export default function DataTable<T>({
    columns,
    data,
    rowKey,
    onRowClick,
    selectedKey,
    initialSort,
    stickyHeader = true,
    expandable,
    pagination = false,
    empty,
    loading = false,
    ariaLabel,
    dense = false,
    className,
}: DataTableProps<T>) {
    const [sorting, setSorting] = useState<SortingState>(
        initialSort ? [{ id: initialSort.id, desc: initialSort.desc }] : [],
    );
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(pagination ? pagination.pageSize : data.length);
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    // Column widths are resizable (TanStack column sizing) and start at
    // min(declared width, the width the longest cell actually needs). The
    // starting width is derived from a one-shot content-measuring pass: render
    // an auto-layout table so each column hugs its widest cell, read those
    // widths, cap them at the declared `width`, then lock in a fixed layout.
    //
    // Sizing is left *uncontrolled* (owned by the table's own state) so the
    // live-resize handler — which writes column sizing from inside its column
    // sizing-info updater — lands in the same store; a separately-controlled
    // useState would drop that nested cross-hook update. Measured widths are
    // pushed imperatively via `table.setColumnSizing` from the effect below.
    const [measuring, setMeasuring] = useState(true);
    // Set by double-clicking a resize handle: the next measuring pass auto-fits
    // just this column to its full content width, then clears.
    const [autoFitColId, setAutoFitColId] = useState<string | null>(null);
    const headerRefs = useRef(new Map<string, HTMLTableCellElement>());
    const measuredSigRef = useRef<string | null>(null);
    const columnIdsSig = columns.map((c) => c.id).join('|');

    // Horizontal-scroll affordance: fade the left/right edge only when content
    // is actually hidden in that direction, so it's clear the table scrolls.
    const scrollRef = useRef<HTMLDivElement>(null);
    const tableRef = useRef<HTMLTableElement>(null);
    const [edges, setEdges] = useState({ left: false, right: false });

    const updateEdges = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const maxScroll = el.scrollWidth - el.clientWidth;
        setEdges({
            left: el.scrollLeft > 1,
            right: el.scrollLeft < maxScroll - 1,
        });
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        updateEdges();
        el.addEventListener('scroll', updateEdges, { passive: true });
        // Re-measure when the viewport (container) or the table content width
        // changes — covers resize, sorting, pagination, and row expansion.
        const ro = new ResizeObserver(updateEdges);
        ro.observe(el);
        if (tableRef.current) ro.observe(tableRef.current);
        return () => {
            el.removeEventListener('scroll', updateEdges);
            ro.disconnect();
        };
    }, [updateEdges]);

    const tableColumns = useMemo<ColumnDef<T>[]>(
        () =>
            columns.map((col) => ({
                id: col.id,
                // accessorFn drives sorting only; cell rendering is done directly
                // from the kit column's `cell` callback below (not via TanStack).
                accessorFn: col.sortValue ? (row: T) => col.sortValue!(row) : undefined,
                enableSorting: Boolean(col.sortable && col.sortValue),
                sortUndefined: 'last',
                size: parsePx(col.width) ?? DEFAULT_COLUMN_PX,
                minSize: parsePx(col.minWidth) ?? MIN_COLUMN_PX,
            })),
        [columns],
    );

    const table = useReactTable({
        data,
        columns: tableColumns,
        state: { sorting },
        onSortingChange: setSorting,
        getRowId: (row) => rowKey(row),
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        manualPagination: true,
        enableSortingRemoval: true,
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
    });

    const sortedRows = table.getRowModel().rows;
    const total = sortedRows.length;

    // Measure → size pass. `measuring` toggles the table into auto layout (each
    // column hugs its content); this layout effect then reads the header cell
    // widths (= the widest cell in that column, header included). It serves two
    // jobs, both pre-paint so the auto-layout frame never shows:
    //   - initial sizing (once per distinct column set): start each column at
    //     min(declared `width` ?? default cap, content), floored at the min;
    //   - double-click auto-fit (`autoFitColId`): size just that column to its
    //     full content width, uncapped, floored at the min.
    useLayoutEffect(() => {
        if (total === 0) {
            // No body rows to content-measure (loading or empty). Leave the
            // auto-layout measuring pass so the header row — and the loading
            // skeleton rows — lay out fixed + min-w-full and fill the
            // container, rather than the table hugging its header text. The
            // signature is deliberately left unrecorded so real data triggers
            // a proper content-measuring pass once it arrives.
            if (measuring) setMeasuring(false);
            return;
        }
        const needInitial = measuredSigRef.current !== columnIdsSig;
        const needAutoFit = autoFitColId !== null;
        if (!needInitial && !needAutoFit) return;
        if (!measuring) {
            setMeasuring(true);
            return;
        }
        const measure = (id: string) => {
            const el = headerRefs.current.get(id);
            return el ? Math.ceil(el.getBoundingClientRect().width) : 0;
        };
        if (needAutoFit) {
            const col = columns.find((c) => c.id === autoFitColId);
            const content = measure(autoFitColId);
            const min = parsePx(col?.minWidth) ?? MIN_COLUMN_PX;
            table.setColumnSizing((old) => ({
                ...old,
                [autoFitColId]: Math.max(min, content || min),
            }));
            setAutoFitColId(null);
            setMeasuring(false);
            return;
        }
        const next: ColumnSizingState = {};
        for (const col of columns) {
            const content = measure(col.id);
            const cap = parsePx(col.width) ?? DEFAULT_MAX_COLUMN_PX;
            const min = parsePx(col.minWidth) ?? MIN_COLUMN_PX;
            next[col.id] = Math.max(min, content > 0 ? Math.min(cap, content) : cap);
        }
        measuredSigRef.current = columnIdsSig;
        table.setColumnSizing(next);
        setMeasuring(false);
    }, [columnIdsSig, total, measuring, columns, table, autoFitColId]);

    // Clamp the current page into range so a shrinking `data` (filter applied,
    // rows removed) never strands the view on an empty page past the end.
    const pageCount = pagination ? Math.max(1, Math.ceil(total / pageSize)) : 1;
    const safePage = Math.min(page, pageCount);

    const paginated = pagination
        ? sortedRows.slice((safePage - 1) * pageSize, safePage * pageSize)
        : sortedRows;

    const rowHeight = dense ? 'h-[34px]' : 'h-table-row';
    const colCount = columns.length + (expandable ? 1 : 0);

    // The leaf header group drives per-column resize handlers. Total width is
    // the sum of column sizes plus the fixed expandable-chevron column (w-9).
    const headerGroup = table.getHeaderGroups()[0];
    const totalWidth = table.getTotalSize() + (expandable ? 36 : 0);

    function toggleExpand(key: string) {
        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
    }

    return (
        <div
            className={cn(
                'overflow-hidden rounded-card border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface))]',
                className,
            )}
        >
            <div className="relative">
                <div ref={scrollRef} className="overflow-x-auto">
                    <table
                        ref={tableRef}
                        aria-label={ariaLabel}
                        style={measuring ? undefined : { width: totalWidth }}
                        className={cn(
                            'border-collapse text-table-body text-[rgb(var(--color-text-primary))]',
                            measuring ? 'table-auto w-max' : 'table-fixed min-w-full',
                        )}
                    >
                        <thead
                            className={cn(
                                'bg-[rgb(var(--color-surface-secondary))]',
                                stickyHeader && 'sticky top-0 z-10',
                            )}
                        >
                            <tr className="border-b border-[rgb(var(--color-divider))]">
                                {expandable && <th className="w-9" aria-hidden="true" />}
                                {columns.map((col) => {
                                    const align = resolveAlign(col);
                                    const tcol = table.getColumn(col.id);
                                    const header = headerGroup?.headers.find(
                                        (h) => h.column.id === col.id,
                                    );
                                    const canSort = Boolean(col.sortable && col.sortValue);
                                    const sortDir = tcol?.getIsSorted();
                                    return (
                                        <th
                                            key={col.id}
                                            ref={(el) => {
                                                if (el) headerRefs.current.set(col.id, el);
                                                else headerRefs.current.delete(col.id);
                                            }}
                                            scope="col"
                                            aria-sort={
                                                canSort
                                                    ? sortDir === 'asc'
                                                        ? 'ascending'
                                                        : sortDir === 'desc'
                                                          ? 'descending'
                                                          : 'none'
                                                    : undefined
                                            }
                                            style={
                                                measuring
                                                    ? undefined
                                                    : {
                                                          width: tcol?.getSize(),
                                                          minWidth: col.minWidth,
                                                      }
                                            }
                                            className={cn(
                                                'relative px-3 py-2 text-table-header uppercase text-[rgb(var(--color-text-tertiary))]',
                                                measuring && 'whitespace-nowrap',
                                                ALIGN[align],
                                            )}
                                        >
                                            {canSort ? (
                                                <button
                                                    type="button"
                                                    onClick={() => tcol?.toggleSorting()}
                                                    className={cn(
                                                        'inline-flex items-center gap-1 select-none',
                                                        'hover:text-[rgb(var(--color-text-secondary))]',
                                                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.4)] rounded',
                                                        align === 'right' && 'flex-row-reverse',
                                                    )}
                                                >
                                                    <span>{col.header}</span>
                                                    <span className="flex h-3 w-3 items-center justify-center">
                                                        {sortDir === 'asc' ? (
                                                            <ChevronUp className="h-3 w-3" />
                                                        ) : sortDir === 'desc' ? (
                                                            <ChevronDown className="h-3 w-3" />
                                                        ) : null}
                                                    </span>
                                                </button>
                                            ) : (
                                                col.header
                                            )}
                                            {!measuring && header && (
                                                <span
                                                    role="separator"
                                                    aria-orientation="vertical"
                                                    title="Drag to resize · double-click to fit to content"
                                                    onMouseDown={header.getResizeHandler()}
                                                    onTouchStart={header.getResizeHandler()}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onDoubleClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        setAutoFitColId(col.id);
                                                    }}
                                                    className="group/resize absolute inset-y-0 right-0 z-10 flex w-2 cursor-col-resize touch-none select-none justify-center"
                                                >
                                                    <span
                                                        className={cn(
                                                            'h-1/2 w-px self-center bg-[rgb(var(--color-divider))] opacity-0 transition-opacity duration-card-hover',
                                                            'group-hover/resize:opacity-100',
                                                            header.column.getIsResizing() &&
                                                                'opacity-100 bg-[rgb(var(--color-accent))]',
                                                        )}
                                                    />
                                                </span>
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {loading
                                ? Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIdx) => (
                                      <tr
                                          key={`skeleton-${rowIdx}`}
                                          aria-hidden="true"
                                          className={cn(
                                              rowHeight,
                                              'border-b border-[rgb(var(--color-divider))] last:border-b-0',
                                          )}
                                      >
                                          {expandable && <td className="w-9" />}
                                          {columns.map((col, colIdx) => {
                                              const align = resolveAlign(col);
                                              return (
                                                  <td
                                                      key={col.id}
                                                      className={cn('px-3 py-1.5', ALIGN[align])}
                                                  >
                                                      <div
                                                          className={cn(
                                                              'h-2.5 rounded bg-[rgb(var(--color-text-primary)/0.08)] animate-pulse',
                                                              SKELETON_WIDTHS[
                                                                  (rowIdx + colIdx) %
                                                                      SKELETON_WIDTHS.length
                                                              ],
                                                              align === 'right' && 'ml-auto',
                                                              align === 'center' && 'mx-auto',
                                                          )}
                                                      />
                                                  </td>
                                              );
                                          })}
                                      </tr>
                                  ))
                                : total === 0
                                  ? null
                                  : paginated.map((row) => {
                                        const key = row.id;
                                        const original = row.original;
                                        const isSelected = selectedKey === key;
                                        // A row is expandable only when `expandable` yields
                                        // real content for it (null/undefined → no chevron,
                                        // no expanded row).
                                        const expandedContent = expandable
                                            ? expandable(original)
                                            : null;
                                        const hasExpansion = expandedContent != null;
                                        const isExpanded = hasExpansion && Boolean(expanded[key]);
                                        const clickable = Boolean(onRowClick);
                                        return (
                                            <Fragment key={key}>
                                                <tr
                                                    aria-selected={isSelected || undefined}
                                                    tabIndex={clickable ? 0 : undefined}
                                                    onClick={
                                                        clickable
                                                            ? () => onRowClick!(original)
                                                            : undefined
                                                    }
                                                    onKeyDown={
                                                        clickable
                                                            ? (e) => {
                                                                  if (
                                                                      e.key === 'Enter' ||
                                                                      e.key === ' '
                                                                  ) {
                                                                      e.preventDefault();
                                                                      onRowClick!(original);
                                                                  }
                                                              }
                                                            : undefined
                                                    }
                                                    className={cn(
                                                        rowHeight,
                                                        'border-b border-[rgb(var(--color-divider))] last:border-b-0',
                                                        'transition-colors duration-card-hover',
                                                        isSelected
                                                            ? 'bg-[rgb(var(--color-accent)/0.06)]'
                                                            : 'hover:bg-[rgb(var(--color-row-hover)/0.025)]',
                                                        clickable &&
                                                            'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[rgb(var(--color-accent)/0.4)]',
                                                    )}
                                                >
                                                    {expandable && (
                                                        <td className="w-9 pl-3">
                                                            {hasExpansion ? (
                                                                <button
                                                                    type="button"
                                                                    aria-expanded={isExpanded}
                                                                    aria-label={
                                                                        isExpanded
                                                                            ? 'Collapse row'
                                                                            : 'Expand row'
                                                                    }
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleExpand(key);
                                                                    }}
                                                                    className="grid h-6 w-6 place-items-center rounded text-[rgb(var(--color-text-tertiary))] hover:bg-[rgb(var(--color-surface-secondary))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.4)]"
                                                                >
                                                                    <ChevronRight
                                                                        className={cn(
                                                                            'h-4 w-4 transition-transform duration-table-expand',
                                                                            isExpanded &&
                                                                                'rotate-90',
                                                                        )}
                                                                    />
                                                                </button>
                                                            ) : null}
                                                        </td>
                                                    )}
                                                    {columns.map((col) => {
                                                        const align = resolveAlign(col);
                                                        return (
                                                            <td
                                                                key={col.id}
                                                                className={cn(
                                                                    'px-3 py-1.5',
                                                                    ALIGN[align],
                                                                    measuring &&
                                                                        'whitespace-nowrap',
                                                                    col.numeric &&
                                                                        'font-mono tabular-nums',
                                                                )}
                                                            >
                                                                {col.cell(original)}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                                {isExpanded && (
                                                    <tr className="border-b border-[rgb(var(--color-divider))] last:border-b-0">
                                                        <td colSpan={colCount} className="p-0">
                                                            <div className="px-3 py-4 bg-[rgb(var(--color-surface-hover)/0.02)]">
                                                                {expandedContent}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        );
                                    })}
                        </tbody>
                    </table>
                </div>
                {/* Edge fades cue horizontal scroll; ink token reads as a soft
                    shadow over any cell background in both light and dark. */}
                <div
                    aria-hidden="true"
                    className={cn(
                        'pointer-events-none absolute inset-y-0 left-0 z-20 w-6 bg-gradient-to-r from-[rgb(var(--color-surface-hover)/0.14)] to-transparent transition-opacity duration-card-hover',
                        edges.left ? 'opacity-100' : 'opacity-0',
                    )}
                />
                <div
                    aria-hidden="true"
                    className={cn(
                        'pointer-events-none absolute inset-y-0 right-0 z-20 w-6 bg-gradient-to-l from-[rgb(var(--color-surface-hover)/0.14)] to-transparent transition-opacity duration-card-hover',
                        edges.right ? 'opacity-100' : 'opacity-0',
                    )}
                />
            </div>
            {/* Empty content renders outside the horizontal scroll wrapper so it
                centres at container width, not table min-width (otherwise the
                copy sits off-screen on narrow viewports). Loading has no footer
                slot — the skeleton rows above carry the loading state. */}
            {!loading && total === 0 ? (
                // Inset the empty slot by the Card body padding (16px) so the
                // EmptyState dotted box sits with the same breathing room it has
                // in a default padded Card — DataTables live in `noPadding` Cards
                // (flush header + rows), which would otherwise butt the box
                // against the table header and card edges.
                <div className="p-4">{empty}</div>
            ) : null}
            {pagination && total > 0 && (
                <Pagination
                    page={safePage}
                    pageSize={pageSize}
                    total={total}
                    onPageChange={setPage}
                    onPageSizeChange={(size) => {
                        setPageSize(size);
                        setPage(1);
                    }}
                />
            )}
        </div>
    );
}
